import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import QuickWidgetDialog from "../../../quickWidgetDialog/quickWidgetDialog";
import { QuickWidgetTypes } from "../../../../reducer/user.reducer";
import { useSelector } from "react-redux";
import { selectPrimarySecurity } from "../../../../selectors/home";
import { selectOverviewSecurity } from "../../../../selectors/security";
import { selectMarketDepthSecurity } from "../../../../selectors/common";
import MarketDepth from "../../../marketDepth/marketDepth";
import { useDispatch } from "react-redux";
import { getOrderBook, fetchSecuritySnapshotsBulk } from "../../../../services/apis/candles";
import { updateOrderBooks } from "../../../../reducer/security.reducer";
import { updateMarketDepthSecurity } from "../../../../reducer/common.reducer";
import { Box, CircularProgress, useTheme } from '@mui/material'
import websocketActions from "../../../../actions/websocket.actions";
import OrderBookOverview from "../../../orderBookOverview/orderBookOverview";
import SymbolDisplay from "../../../symbolDisplay/symbolDisplay";
import { getCmTwinOnExchange, getExchangesForSecurity, getSecurityAsync, getSecurityKey } from "../../../../util/security"
import { Security, SecurityCategory_CategoryCode } from "../../../../proto/generated/security";
import { isTradeableSecurity } from "../../../../util/cashInstrument";
import { useLocation } from "react-router-dom";
import { Exchange } from "../../../../proto/generated/common";
import QuickTradeExchangeSelector from "../../../quickTradeWindow/QuickTradeExchangeSelector";

const MarketDepthWidget: React.FC = () => {

    const theme = useTheme()
    const dispatch = useDispatch();
    const location = useLocation();
    const { primary: security, expressionMode, expressionSecurities, key } = useSelector(selectPrimarySecurity)
    const marketDepthSecurity = useSelector(selectMarketDepthSecurity)
    const overviewSecurityKey = useSelector(selectOverviewSecurity)
    
    const isScreenerPage = location.pathname.includes('/screener')
    
    const [displaySecurity, setDisplaySecurity] = useState<Security | undefined>(undefined)
    const [fallbackSecurity, setFallbackSecurity] = useState<Security | undefined>(undefined)
    const niftyFallbackRun = useRef(false)

    const securityToUse = useMemo(() => {
        if (isScreenerPage && overviewSecurityKey) {
            return overviewSecurityKey
        } else if (marketDepthSecurity) {
            return marketDepthSecurity
        } else if (security) {
            return security
        } else {
            return fallbackSecurity
        }
    }, [isScreenerPage, overviewSecurityKey, marketDepthSecurity, security, fallbackSecurity])

    useEffect(() => {
        if (!security && !marketDepthSecurity && !isScreenerPage && !fallbackSecurity && !niftyFallbackRun.current) {
            niftyFallbackRun.current = true
            const timer = setTimeout(() => {
                getSecurityAsync('NSE:NIFTY_50').then(sec => {
                    if (sec) {
                        setFallbackSecurity(sec)
                    }
                })
            }, 50)
            return () => clearTimeout(timer)
        }
    }, [security, marketDepthSecurity, isScreenerPage, fallbackSecurity])

    useEffect(() => {
        if ((security || marketDepthSecurity) && fallbackSecurity) {
            setFallbackSecurity(undefined)
            niftyFallbackRun.current = false
        }
    }, [security, marketDepthSecurity, fallbackSecurity])

    useEffect(() => {
        if (securityToUse) {
            if (typeof securityToUse === 'string') {
                getSecurityAsync(securityToUse).then(sec => {
                    setDisplaySecurity(sec)
                })
            } else {
                setDisplaySecurity(securityToUse)
            }
        } else {
            setDisplaySecurity(undefined)
        }
    }, [securityToUse])


    useEffect(() => {
        if (displaySecurity) {
            const securitiesToSubscribe = getExchangesForSecurity(displaySecurity).map((exchange) => {
                const twin = getCmTwinOnExchange(displaySecurity, exchange)
                return {
                    exchange,
                    code: twin?.code ?? displaySecurity.code,
                }
            })

            getOrderBook({
                security: displaySecurity,
                dispatch: dispatch
            })
                .then((result) => {
                    const orderBook = { ...result }
                    if (!orderBook.security) {
                        orderBook.security = { ...displaySecurity }
                    }
                    dispatch(updateOrderBooks([orderBook]))
                })
                .catch((error) => {
                    console.error('Error fetching order book:', error)
                })

            if (securitiesToSubscribe.length > 0) {
                fetchSecuritySnapshotsBulk({
                    securities: securitiesToSubscribe,
                    dispatch,
                }).catch((error) => {
                    console.error('Error fetching exchange snapshots for market depth:', error)
                })
            }

            const securityRef = {
                exchange: displaySecurity.exchange,
                code: displaySecurity.code
            }
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [securityRef],
                        topBidAskOnly: false
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: securitiesToSubscribe.length > 0 ? securitiesToSubscribe : [securityRef]
                    }
                ]
            })
        }
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [],
                        topBidAskOnly: false
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: []
                    }
                ]
            })
        }
    }, [displaySecurity, dispatch])

    const availableExchanges = useMemo(
        () => (displaySecurity ? getExchangesForSecurity(displaySecurity) : []),
        [displaySecurity]
    )

    const handleExchangeChange = useCallback((exchange: Exchange) => {
        if (!displaySecurity) return
        const twin = getCmTwinOnExchange(displaySecurity, exchange)
        if (twin) {
            dispatch(updateMarketDepthSecurity(twin))
        }
    }, [displaySecurity, dispatch])

    const title = useMemo(() => {
        return (
            <Box display="flex" alignItems="center" minWidth={0} width="100%" pr={1} gap={0.5}>
                <Box display="flex" alignItems="center" minWidth={0} overflow="hidden" gap={0.5} flexShrink={1}>
                    <SymbolDisplay
                        securityList={expressionMode ? expressionSecurities : (displaySecurity ? [displaySecurity] : [])}
                        expression={key || ''}
                        expressionMode={expressionMode}
                        expressionName={''}
                        fontSize={12}
                        logoSize={20}
                        maxWidth="auto"
                        displayEvents={false}
                    />
                    {!expressionMode && displaySecurity && (
                        <QuickTradeExchangeSelector
                            availableExchanges={availableExchanges}
                            selectedSecurity={displaySecurity}
                            type="trade"
                            onExchangeChange={handleExchangeChange}
                        />
                    )}
                </Box>
            </Box>
        )
    }, [expressionMode, key, expressionSecurities, displaySecurity, availableExchanges, handleExchangeChange])


    return <QuickWidgetDialog widgetType={QuickWidgetTypes.MARKET_DEPTH} title={title} >

        {expressionMode ?
            <Box padding={4} textAlign="center">Depth not available for expressions.</Box> : (
                displaySecurity ? (
                    <>
                        {displaySecurity.categoryCode !== SecurityCategory_CategoryCode.INDEX && (
                            <Box padding={theme.spacing(1, 1, 0.5)}>
                                <MarketDepth security={displaySecurity} tradeable={isTradeableSecurity(displaySecurity)} />
                            </Box>
                        )}
                        <Box padding={theme.spacing(1)}>
                            <OrderBookOverview security={displaySecurity} />
                        </Box>
                    </>
                ) : <Box padding={4} textAlign="center"><CircularProgress size={20} /></Box>
            )
        }

    </QuickWidgetDialog>
}

export default memo(MarketDepthWidget);
